<!--
  EventCalendar:F-0 场次月视图日历(UI-SPEC §5.1)
  - events prop 由父组件注入(来源 GET /inventory/redeemable-events，shared 包零业务依赖)
  - 空 events → 渲染当前月份空态，但保留年月头和切月按钮
  - SUN 起首;sliding buttons 切换上/下月
  - 点可点格 → emit 'event-selected' 把完整 event 上抛;wish 由父弹出 wishlist 详情
-->
<template>
  <div class="event-calendar" :class="`page-${pageType}`">
    <!-- ─── 年月 + 左右切月 ─────────────────────────────── -->
    <div class="year-month-row">
      <div class="year-month">
        <span class="year">{{ currentYear }}</span>
        <span class="month-chip">
          <span class="month-text">{{ monthLabel }}</span>
        </span>
      </div>
      <div class="slide-actions">
        <button
          type="button"
          class="slide-btn left"
          @click="prevMonth"
          aria-label="Previous month"
        ></button>
        <button
          type="button"
          class="slide-btn right"
          @click="nextMonth"
          aria-label="Next month"
        ></button>
      </div>
    </div>

    <!-- ─── 标题卡 ─────────────────────────────────────── -->
    <div class="title-card">
      <div class="title">{{ t('Modal.gameSchedule') }}</div>
      <div class="subtitle">{{ t('Modal.gameScheduleSub') }}</div>
    </div>

    <!-- ─── 周表头 SUN..SAT ─────────────────────────────── -->
    <div class="weekdays">
      <div
        v-for="d in weekdayLabels"
        :key="d"
        class="weekday"
      >
        {{ d }}
      </div>
    </div>

    <!-- ─── 网格 ────────────────────────────────────────── -->
    <div class="grid">
      <CalendarDayCell
        v-for="(cell, idx) in grid"
        :key="idx"
        :cell="cell"
        :variant="cellVariant(cell)"
        :page-type="pageType"
        @click="onCellClick"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import CalendarDayCell from './CalendarDayCell.vue';
import { buildMonthGrid, cellVariant, type CalendarCell, type EventCalendarItem } from '../utils/calendar';
import t from '../utils/i18n';
type Props = {
  events: EventCalendarItem[];
  pageType: 'PC' | 'H5';
  /** 当前展示月份，格式 YYYY-MM，由业务父组件持有并驱动按月请求。 */
  month: string;
};
const __sfc_main = {};
__sfc_main.props = {
  events: {
    key: "events",
    required: true,
    type: Array
  },
  pageType: {
    key: "pageType",
    required: true,
    type: String
  },
  month: {
    key: "month",
    required: true,
    type: String
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;

  // 直接 emit 整个 EventItem,保留 teamText / opponentFull 等字段,
  // 避免 RedeemTicketModal 再次 find(race condition 下会丢 Match 行)
  const emit = __ctx.emit;

  // 周表头:SUN..SAT(UI-SPEC §5.1.b 首列 SUN)
  const weekdayLabels = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  function parseMonth(month: string): {
    y: number;
    m: number;
  } {
    const matched = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
    if (matched) {
      return {
        y: Number(matched[1]),
        m: Number(matched[2])
      };
    }
    const now = new Date();
    return {
      y: now.getFullYear(),
      m: now.getMonth() + 1
    };
  }
  const currentYear = computed(() => parseMonth(props.month).y);
  const currentMonth = computed(() => parseMonth(props.month).m);
  const grid = computed<CalendarCell[]>(() => buildMonthGrid(currentYear.value, currentMonth.value, props.events));
  const monthLabel = computed(() => {
    const d = new Date(currentYear.value, currentMonth.value - 1, 1);
    // 用英文缩写;UI-SPEC §4.9 不声明 font-family,字体栈继承项目
    return d.toLocaleString('en-US', {
      month: 'short'
    }).toUpperCase();
  });
  function prevMonth(): void {
    let nextYear = currentYear.value;
    let nextMonth = currentMonth.value;
    if (currentMonth.value === 1) {
      nextYear -= 1;
      nextMonth = 12;
    } else {
      nextMonth -= 1;
    }
    emitMonthChange(nextYear, nextMonth);
  }
  function nextMonth(): void {
    let nextYear = currentYear.value;
    let nextMonth = currentMonth.value;
    if (currentMonth.value === 12) {
      nextYear += 1;
      nextMonth = 1;
    } else {
      nextMonth += 1;
    }
    emitMonthChange(nextYear, nextMonth);
  }
  function emitMonthChange(year: number, month: number): void {
    emit('month-change', `${year}-${String(month).padStart(2, '0')}`);
  }
  function onCellClick(cell: CalendarCell): void {
    if (!cell.event) return;
    // emit 整个 EventItem,保留 teamText/opponentFull 等字段(WR-02)
    emit('event-selected', cell.event);
  }
  return {
    cellVariant,
    t,
    weekdayLabels,
    currentYear,
    grid,
    monthLabel,
    prevMonth,
    nextMonth,
    onCellClick
  };
};
__sfc_main.components = Object.assign({
  CalendarDayCell
}, __sfc_main.components);
export default __sfc_main;
</script>

<style scoped>
.event-calendar {
  background: linear-gradient(180deg, #0a489d, #042258);
  color: #ffffff;
  box-sizing: border-box;
  margin: 0 auto;
}
.year-month-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.year-month {
  display: flex;
  align-items: center;
}
.slide-actions {
  display: flex;
  align-items: center;
}
.year {
  color: #ffffff;
  font-weight: 400;
  font-family: "Avenir Next Condensed", "HelveticaNeue-CondensedBold", "Arial Narrow", "Roboto Condensed", sans-serif-condensed, sans-serif;
}
.month-chip {
  background: linear-gradient(180deg, #ff7721, #ff4b01);
  color: #ffffff;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  overflow: hidden;
  box-sizing: border-box;
}
.month-text {
  display: block;
  font-family: "Avenir Next Condensed", "HelveticaNeue-CondensedBold", "Arial Narrow", "Roboto Condensed", sans-serif-condensed, sans-serif;
  font-weight: 700;
  line-height: 1em;
}
.slide-btn {
  background: url('../assets/btn-calendar-arrow-left.png') center / contain no-repeat;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.slide-btn.right {
  transform: rotate(180deg);
}

.title-card {
  background: #042e75 url('../assets/bg-calendar-schedule-header.png') center / cover no-repeat;
  text-align: center;
  box-sizing: border-box;
  overflow: hidden;
}
.title {
  color: #ffffff;
  font-weight: 600;
  letter-spacing: -0.02em;
}
.subtitle {
  color: #ffffff;
  font-weight: 500;
}

.weekdays {
  display: grid;
  background: #042e75;
  box-sizing: border-box;
}
.weekday {
  text-align: center;
  color: #ffffff;
  font-weight: 400;
  background: #0041ac;
  box-sizing: border-box;
}

.grid {
  display: grid;
  background: #042e75;
  box-sizing: border-box;
}

/* ─── H5(rem,UI-SPEC §5.1) ─────────────────────────────── */
.event-calendar.page-H5 {
  width: 6.74rem;
  margin-bottom: 0.42rem;
}
.page-H5 .year-month-row {
  gap: 1.49rem;
  margin-bottom: 0.24rem;
}
.page-H5 .year-month {
  gap: 0.12rem;
}
.page-H5 .slide-actions {
  gap: 0.1rem;
}
.page-H5 .year {
  font-size: 0.8rem;
  line-height: 0.8em;
  letter-spacing: -0.02em;
}
.page-H5 .month-chip {
  min-width: 1.28rem;
  height: 0.64rem;
  padding: 0 0.12rem;
  border-radius: 0;
}
.page-H5 .month-text {
  font-size: 0.58rem;
  letter-spacing: -0.02em;
}
.page-H5 .slide-btn {
  width: 0.8rem;
  height: 0.8rem;
}
.page-H5 .title-card {
  width: 6.74rem;
  height: 1.5rem;
  padding-top: 0.15rem;
}
.page-H5 .title {
  font-size: 0.56rem;
  line-height: 1.2em;
}
.page-H5 .subtitle {
  font-size: 0.36rem;
  line-height: 1.35em;
  margin-top: 0.04rem;
}
.page-H5 .weekdays {
  grid-template-columns: repeat(7, 0.92rem);
  gap: 0.04rem;
  width: 6.74rem;
  padding: 0.03rem 0.03rem 0;
}
.page-H5 .weekdays .weekday {
  width: 0.92rem;
  height: 0.48rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.24rem;
  line-height: 1.35em;
}
.page-H5 .grid {
  grid-template-columns: repeat(7, 0.92rem);
  gap: 0.04rem;
  width: 6.74rem;
  padding: 0.04rem 0.03rem 0.03rem;
}

/* ─── PC(UI-SPEC §5.1) ──────────────────────────────────── */
.event-calendar.page-PC {
  width: 444px;
  background: transparent;
}
.page-PC .year-month-row {
  gap: 97px;
  margin-bottom: 16px;
}
.page-PC .year-month {
  gap: 10px;
}
.page-PC .slide-actions {
  gap: 6px;
}
.page-PC .year {
  font-size: 52px;
  line-height: 0.8em;
  letter-spacing: -0.02em;
}
.page-PC .month-chip {
  min-width: 86px;
  height: 41.6px;
  padding: 0 8px;
  border-radius: 0;
}
.page-PC .month-text {
  font-size: 34px;
  letter-spacing: -0.02em;
}
.page-PC .slide-btn {
  width: 52px;
  height: 52px;
}
.page-PC .title-card {
  width: 444px;
  height: 98px;
  padding-top: 10px;
}
.page-PC .title {
  font-size: 36px;
  line-height: 1.2em;
}
.page-PC .subtitle {
  font-size: 22px;
  line-height: 1.35em;
  margin-top: 2px;
}
.page-PC .weekdays {
  grid-template-columns: repeat(7, 60px);
  gap: 3px;
  width: 444px;
  padding: 3px 3px 0;
}
.page-PC .weekdays .weekday {
  width: 60px;
  height: 31px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1.35em;
}
.page-PC .grid {
  grid-template-columns: repeat(7, 60px);
  gap: 3px;
  width: 444px;
  padding: 3px;
}
</style>
